import { createRouter, createWebHistory } from "vue-router";
import HomePage from "../pages/HomePage.vue";
import { apiFetch } from "../lib/api";
import { getCurrentUser } from "../lib/current-user";
import { applyRouteSeo } from "../lib/seo";

const routes = [
  {
    path: "/",
    component: HomePage,
    meta: { redirectIfAuthenticated: true },
  },
  {
    path: "/login",
    component: () => import("../pages/LoginPage.vue"),
    meta: { redirectIfAuthenticated: true },
  },
  { path: "/signup", component: () => import("../pages/SignupPage.vue") },
  {
    path: "/dashboard",
    component: () => import("../pages/DashboardPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/routes/new",
    component: () => import("../pages/NewRoutePage.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/routes/:routeId",
    name: "saved-permit-route",
    component: () => import("../pages/NewRoutePage.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/forgot-password",
    component: () => import("../pages/ForgotPasswordPage.vue"),
  },
  {
    path: "/reset-password",
    component: () => import("../pages/ResetPasswordPage.vue"),
  },
  { path: "/verify", component: () => import("../pages/VerifyPage.vue") },
  {
    path: "/admin",
    component: () => import("../pages/admin/AdminPage.vue"),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/account",
    component: () => import("../pages/AccountPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/organizations/:organizationId/settings",
    component: () => import("../pages/OrganizationSettingsPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/routes",
    component: () => import("../pages/RouteHistoryPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/planner",
    component: () => import("../pages/RoutePlannerPage.vue"),
    meta: { requiresAuth: true },
  },
  { path: "/terms", component: () => import("../pages/TermsPage.vue") },
  { path: "/privacy", component: () => import("../pages/PrivacyPage.vue") },
  {
    path: "/disclaimer",
    component: () => import("../pages/DisclaimerPage.vue"),
  },
  { path: "/pricing", component: () => import("../pages/PricingPage.vue") },
  { path: "/s", component: () => import("../pages/SharedRoutePage.vue") },
  {
    path: "/admin/shared-route/:adminShareId",
    component: () => import("../pages/SharedRoutePage.vue"),
    props: true,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to) {
    if (to.path === "/s") return { top: 0 };
    if (to.hash) return { el: to.hash, behavior: "smooth" };
    return { top: 0 };
  },
});

router.beforeEach(async (to, from) => {
  if (
    ["/dashboard", "/routes/new"].includes(to.path) &&
    typeof to.query.route === "string" &&
    to.query.route &&
    to.query.new !== "1"
  ) {
    const query = { ...to.query };
    delete query.route;
    return {
      name: "saved-permit-route",
      params: { routeId: to.query.route },
      query,
      hash: to.hash,
      replace: true,
    };
  }
  if (to.path === "/dashboard" && to.query.new === "1")
    return {
      path: "/routes/new",
      query: to.query,
      hash: to.hash,
      replace: true,
    };

  if (to.meta.requiresAuth && Object.hasOwn(to.query, "workspace")) {
    const query = { ...to.query };
    delete query.workspace;
    return { path: to.path, query, hash: to.hash, replace: true };
  }
  if (!to.meta.requiresAuth && !to.meta.redirectIfAuthenticated) return true;

  let user = null;
  try {
    const refresh = await apiFetch("/api/auth/refresh", {
      method: "POST",
      headers: { "X-Requested-With": "PermitPilot" },
    });
    if (refresh.ok) user = await getCurrentUser();
  } catch {}

  if (to.meta.redirectIfAuthenticated) {
    return user ? { path: "/dashboard", replace: true } : true;
  }

  if (!user) {
    if (from.meta.requiresAuth) {
      window.dispatchEvent(new Event("pp-session-expired"));
      return false;
    }
    return {
      path: "/login",
      query: { redirect: to.fullPath },
    };
  }

  if (to.meta.requiresAdmin && !user.isAdmin) {
    return { path: "/dashboard" };
  }

  return true;
});

router.afterEach((to) => {
  applyRouteSeo(to);
});

export default router;
